<template>
	<TheForm :model="request" :rules="rules" :id="id" ref="formRef" :persistForm="true" formNamespace="faelleFilter" :autoSave="true">
		<el-row>
			<el-form-item label="Freitextsuche" prop="searchTerm" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
				<el-input v-model="request.searchTerm" placeholder="Suche" clearable />
			</el-form-item>

			<el-col :sm="12" :xs="24">
				<el-row>
					<el-form-item label="Beschwerdegrund" prop="category" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
						<el-select v-model="request.category" placeholder="Alle">
							<el-option v-for="item in categoryOptions" :key="item.value" :label="item.label" :value="item" />
						</el-select>
					</el-form-item>
					<el-form-item label="Medium" prop="medium" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
						<el-select v-model="request.medium" placeholder="Alle">
							<el-option v-for="item in mediaOptions" :key="item.value" :label="item.label" :value="item" />
						</el-select>
					</el-form-item>
				</el-row>
			</el-col>
		</el-row>

		<el-row>
			<el-col :sm="12" :xs="24">
				<el-row>
					<el-form-item label="Branche" prop="sector" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
						<el-select v-model="request.sector" placeholder="Alle">
							<el-option v-for="item in sectorOptions" :key="item.value" :label="item.label" :value="item" />
						</el-select>
					</el-form-item>
					<el-form-item label="Bundesland" prop="reach" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
						<el-select v-model="request.reach" placeholder="Alle">
							<el-option v-for="item in reachOptions" :key="item.value" :label="item.label" :value="item" />
						</el-select>
					</el-form-item>
				</el-row>
			</el-col>

			<el-col :sm="12" :xs="24">
				<el-row>
					<el-form-item label="Status" prop="states" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
						<el-select v-model="request.states" placeholder="Alle">
							<el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item" />
						</el-select>
					</el-form-item>
					<el-form-item label="Veröffentlicht" prop="isPublished" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
						<el-select v-model="request.isPublished" placeholder="Alle">
							<el-option v-for="item in isPublicOptions" :key="item.value" :label="item.label" :value="item" />
						</el-select>
					</el-form-item>
				</el-row>
			</el-col>
		</el-row>

		<el-row>
			<el-form-item
				:label="!forStatistics ? 'Zeitraum (Erstellung)' : 'Zeitraum (Veröffentlichung)'"
				prop="timePeriod"
				class="el-col el-col-24 el-col-sm-12 el-col-xs-24"
			>
				<el-checkbox-group v-model="selectedTimePeriod">
					<el-checkbox
						v-for="item in timePeriodOptions"
						:key="item.value"
						:value="item.value"
						:border="true"
						@change="() => handleClickOptionWrapper(item)"
					>
						{{ item.label }}
					</el-checkbox>
				</el-checkbox-group>
			</el-form-item>
			<el-col :sm="4" :xs="24"></el-col>
			<el-form-item label="" prop="dateRange" class="el-col el-col-24 el-col-sm-8 el-col-xs-24">
				<div class="datepicker-range__labels">
					<label class="el-form-item__label">Von</label>
					<label class="el-form-item__label">Bis</label>
				</div>
				<el-date-picker
					v-model="dateRange"
					type="daterange"
					:format="'DD.MM.YYYY'"
					range-separator=""
					:clearable="true"
					start-placeholder="TT.MM.JJJJ"
					end-placeholder="TT.MM.JJJJ"
				/>
			</el-form-item>
		</el-row>
		<el-row class="button-panel">
			<TheButton type="danger" @buttonclick="$emit('doSearch', true)" isSubmit>Anwenden</TheButton>
			<TheButton type="default" @buttonclick="resetSearch">
				<el-icon><Close /></el-icon>
				Filter zurücksetzen
			</TheButton>
		</el-row>
	</TheForm>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { returnFormOptions } from '@/utils/formOptions.js';
import { handleClickTimePeriodOptionCheckbox, handleSearchDateRangeChange } from '@/utils/search/searchUtils';
import { resetForm } from '@/utils/functions';
import { SearchCaseRequest } from '@/types/types';
import moment from 'moment';
import { Close } from '@element-plus/icons-vue';

import TheForm from '@/components/TheForm.vue';
import TheButton from '@/components/TheButton.vue';
import { FormInstance } from 'element-plus';

interface SearchCasesData {
	request: SearchCaseRequest;
	rules: Object;
	dateRange: string[] | null;
	selectedTimePeriod: any[];
	tablePage: number;
	itemsPerPage: number;
}

export default defineComponent({
	name: 'FaelleSearchForm',
	data(): SearchCasesData {
		return {
			request: {
				searchTerm: '',
				category: null,
				medium: null,
				states: null,
				isPublished: null,
				fromDate: null,
				toDate: null,
				reach: null,
				sector: null,
			},
			rules: {},
			dateRange: null,
			selectedTimePeriod: [],
			tablePage: 0,
			itemsPerPage: 14,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
		requestInitial: {
			type: Object,
			default: {},
		},
		forStatistics: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		TheForm,
		TheButton,
		Close,
	},
	setup() {
		const formRef = ref<FormInstance>();
		return {
			formRef,
		};
	},
	emits: ['doSearch'],
	computed: {
		categoryOptions() {
			return returnFormOptions('complaintAndCaseCategories', false, true);
		},
		mediaOptions() {
			return returnFormOptions('media', false, true);
		},
		sectorOptions() {
			return returnFormOptions('sector', false, true);
		},
		reachOptions() {
			return returnFormOptions('reach', false, true);
		},
		statusOptions() {
			return returnFormOptions('caseStateOptions', false, true);
		},
		isPublicOptions() {
			return returnFormOptions('isPublic', false, true);
		},
		timePeriodOptions() {
			return returnFormOptions('timePeriod');
		},
	},
	methods: {
		handleClickOptionWrapper(item) {
			const { newSelectedTimePeriod, newTimePeriodOptions, newDateRange } = handleClickTimePeriodOptionCheckbox(
				item,
				this.selectedTimePeriod,
				this.timePeriodOptions,
			);

			this.selectedTimePeriod = newSelectedTimePeriod;
			this.timePeriodOptions = newTimePeriodOptions;
			this.dateRange = newDateRange;
		},

		resetSearch() {
			this.dateRange = null;
			resetForm(this.formRef, this.id);
			this.$emit('doSearch', true);
		},
	},
	mounted() {
		if (this.$route.query.searchTerm) {
			this.request.searchTerm = String(this.$route.query.searchTerm);
		}

		if (this.$route.query.months) {
			switch (this.$route.query.months) {
				case '1':
					this.dateRange = [moment().startOf('month').toString(), moment().endOf('day').toString()];
					this.selectedTimePeriod = ['month'];
					break;
				case '3':
					this.dateRange = [moment().subtract(3, 'months').startOf('month').toString(), moment().endOf('day').toString()];
					break;
				case '6':
					this.dateRange = [moment().subtract(6, 'months').startOf('month').toString(), moment().endOf('day').toString()];
					break;
				case '12':
					this.dateRange = [moment().subtract(12, 'months').startOf('month').toString(), moment().endOf('day').toString()];
					break;
			}
		}

		if (this.requestInitial) {
			this.request = {
				...this.request,
				...this.requestInitial,
			};
		}
	},
	watch: {
		dateRange: {
			handler(val) {
				if (val && val.length === 2) {
					const { fromDate, toDate } = handleSearchDateRangeChange(val);
					this.request.fromDate = fromDate;
					this.request.toDate = toDate;
				} else {
					this.request.fromDate = null;
					this.request.toDate = null;
					this.selectedTimePeriod = [];
					this.timePeriodOptions.forEach((option: any) => (option.checked = false));
				}
			},
			deep: true,
		},
		request: {
			handler() {
				if (this.request.fromDate && this.request.toDate) {
					this.dateRange = [this.request.fromDate, this.request.toDate];
				}
			},
			deep: true,
		},
	},
});
</script>
